import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useProjectsStoreApi } from '../stores/projectsContext';
import { getAssignment } from '../teacher/teacherApi';
import { useClassroomAssignments, useClassroomSummary } from '../teacher/useTeacher';
import { apiBaseUrl, AuthApiError } from '../account/authApi';
import './ClassroomBanner.css';

/**
 * Banner the kid sees on Home when they're signed into a classroom.
 *
 * Three jobs:
 *   * Tell the kid which class they're in ("Period 3 — Image
 *     classifier") so the studio doesn't feel disconnected from
 *     the room they joined.
 *   * Surface the active pinned assignments so they know what to
 *     work on. One click starts a project from the template.
 *   * Link to the class gallery so kids can see classmates' work.
 *
 * This component is rendered ONLY when the user has a `classroom_id`.
 * Other accounts (parents, free-play kids, teachers) see Home as
 * before — the banner is invisible to them.
 */
export function ClassroomBanner({ classroomId }: { classroomId: string }): JSX.Element | null {
  const summary = useClassroomSummary(classroomId);
  const assignments = useClassroomAssignments(classroomId);
  const navigate = useNavigate();
  const store = useProjectsStoreApi();

  const startFromAssignment = useMutation({
    mutationFn: async (assignmentId: string) => {
      // Pre-fetch the assignment detail so we know the default class
      // labels + starter block program. Without this, the local
      // project lands empty even though the curated unit defined
      // sensible class names ("yes" / "no", "happy" / "sad", etc.).
      const assignment = await getAssignment(assignmentId);

      // Then create the server-side project from the assignment.
      // We do this BEFORE creating the local one so any backend
      // failure leaves no half-created local artefact.
      const r = await fetch(`${apiBaseUrl()}/projects`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ from_assignment_id: assignmentId }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => null)) as { detail?: string } | null;
        throw new AuthApiError(r.status, body?.detail ?? null, 'Could not start the assignment.');
      }
      const project = (await r.json()) as {
        id: string;
        name: string;
        kind: 'image' | 'audio' | 'pose';
      };
      return { project, assignment };
    },
    onSuccess: async ({ project, assignment }) => {
      // Mirror into the local studio store so the kid sees their
      // project alongside any other local work. Pre-seed the
      // assignment's default class labels — otherwise the kid drops
      // into an empty workspace and has to invent labels cold.
      const local = await store.getState().create({
        name: project.name,
        kind: project.kind,
      });
      // Bind the local project to its server twin BEFORE seeding
      // classes, so any race in the studio knows the mirror already
      // exists — prevents the SyncButton from creating a duplicate.
      await store.getState().setServerId(local.id, project.id);
      for (const label of assignment.default_class_labels) {
        await store.getState().addClass(local.id, label);
      }
      navigate(`/studio/p/${local.id}`);
    },
  });

  if (!summary.data && !assignments.data) {
    // Loading silently — Home renders the projects list right
    // below, so a flash of blank is fine.
    return null;
  }

  const active = (assignments.data ?? []).filter((a) => a.status === 'active');

  return (
    <section className="bl-class-banner" aria-label="Your class" data-testid="classroom-banner">
      <div className="bl-class-banner__head">
        <div className="bl-class-banner__title-block">
          <span className="bl-class-banner__eyebrow">Your class</span>
          <h2 className="bl-class-banner__title">{summary.data?.name ?? 'Your classroom'}</h2>
        </div>
        <Link to={`/class/${classroomId}/gallery`} className="bl-class-banner__gallery-link">
          Class gallery →
        </Link>
      </div>

      {active.length === 0 ? (
        <p className="bl-class-banner__quiet">
          No assignments pinned yet — your teacher will add one when they&apos;re ready.
        </p>
      ) : (
        <ul className="bl-class-banner__assignments">
          {active.map((a) => (
            <li key={a.id} className="bl-class-banner__row">
              <div>
                <div className="bl-class-banner__row-name">{a.assignment_name}</div>
                <div className="bl-class-banner__row-meta">
                  {a.assignment_kind}
                  {a.assignment_time_estimate_minutes
                    ? ` · ~${a.assignment_time_estimate_minutes} min`
                    : ''}
                </div>
              </div>
              <button
                type="button"
                className="bl-class-banner__start"
                onClick={() => startFromAssignment.mutate(a.assignment_id)}
                disabled={startFromAssignment.isPending}
              >
                {startFromAssignment.isPending ? 'Starting…' : 'Start →'}
              </button>
            </li>
          ))}
        </ul>
      )}
      {startFromAssignment.error instanceof Error ? (
        <p className="bl-class-banner__error" role="alert">
          {startFromAssignment.error.message}
        </p>
      ) : null}
    </section>
  );
}
